import en from "./en";
import hi from "./hi";

export type LangCode = "en" | "hi";

export const DEFAULT_LANG: LangCode = "en";

/*
 * NOTE — switching languages does NOT happen here any more.
 *
 * The site is translated at runtime by the Google Website Translator. The
 * language list, the `googtrans` cookie and the switch itself all live in
 * `lib/googleTranslate.ts`; what is left in this file is the lookup that
 * serves the English copy the widget then translates.
 *
 * `hi` is consequently unused at runtime. It stays exported so the parity
 * suite can keep checking it against `en`, and so any page can be moved back
 * to the hand-written Hindi without digging it out of git history.
 */
export const dictionaries = { en, hi };

export type Dictionary = typeof en;

/**
 * Look a dot-path up in a dictionary. Returns `undefined` rather than throwing
 * on a missing or half-missing path, so `translate` below can fall through to
 * English.
 */
const lookup = (dict: unknown, path: string): unknown =>
  path.split(".").reduce<unknown>((node, key) => {
    if (node === null || typeof node !== "object") return undefined;
    return (node as Record<string, unknown>)[key];
  }, dict);

/**
 * Resolve `path` in `lang`, falling back to English and finally to the path
 * itself.
 *
 * The English fallback is the important one: `hi.ts` is typed as `typeof en`
 * so a missing key can't reach production, but a *runtime* path typo — `t()`
 * takes a string, not a key union — would otherwise render the raw path to a
 * visitor. Falling back keeps the page readable and leaves the path visible in
 * the one case nothing resolves, which is what you want while developing.
 *
 * Values are whole nodes, not just leaves: sections that repeat (FAQ
 * categories, the five showcase panels) live in the dictionary as arrays and
 * objects so a translator edits one structure rather than forty numbered keys.
 */
export function translate<T = string>(lang: LangCode, path: string): T {
  const hit = lookup(dictionaries[lang], path);
  if (hit !== undefined) return hit as T;

  const fallback = lookup(dictionaries[DEFAULT_LANG], path);
  if (fallback !== undefined) return fallback as T;

  return path as unknown as T;
}

/** Fills `{name}` placeholders — `t("roi.moneyBackNote")` and friends. */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match
  );
}
